"use client";

import * as api from "@/lib/auth-api";
import { dialCodeOf } from "@/lib/auth-flow";
import { queryClient } from "@/lib/query-client";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AuthField, AuthInput, OrDivider, PasswordInput, PhoneInput, SubmitButton, inputClass } from "./auth-ui";
import { GoogleButton } from "./google-button";

type RegisterValues = {
  name: string;
  email: string;
  phone: string;
  phone_country_code: string;
  gender: "male" | "female";
  password: string;
  password_confirmation: string;
};

export function RegisterForm({ redirect }: { redirect: string }) {
  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RegisterValues>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      phone_country_code: "ID",
      gender: "male",
      password: "",
      password_confirmation: "",
    },
  });

  const mutation = useMutation(
    (v: RegisterValues) => api.register({ ...v, phone_country_code: dialCodeOf(v.phone_country_code) }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["auth"]);
        window.location.href = redirect;
      },
      onError: (e) => toast.error(api.getErrorMessage(e)),
    },
  );

  const server = api.getServerErrors(mutation.error);
  const err = (field: keyof RegisterValues) => errors[field]?.message ?? server[field];

  return (
    <form noValidate onSubmit={handleSubmit((v) => mutation.mutate(v))} className="grid gap-4">
      <AuthField label="Nama Lengkap" htmlFor="name" error={err("name")}>
        <AuthInput
          id="name"
          autoComplete="name"
          placeholder="John Doe"
          invalid={!!err("name")}
          {...register("name", { required: "Nama lengkap wajib diisi" })}
        />
      </AuthField>

      <AuthField label="Email" htmlFor="email" error={err("email")}>
        <AuthInput
          id="email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          placeholder="name@example.com"
          invalid={!!err("email")}
          {...register("email", { required: "Email wajib diisi" })}
        />
      </AuthField>
      <AuthField label="Nomor Telepon" htmlFor="phone" error={err("phone")}>
        <Controller
          control={control}
          name="phone_country_code"
          render={({ field }) => (
            <PhoneInput
              countryCode={field.value}
              onCountryChange={field.onChange}
              id="phone"
              invalid={!!err("phone")}
              inputProps={register("phone", { required: "Nomor telepon wajib diisi" })}
            />
          )}
        />
      </AuthField>

      <AuthField label="Jenis Kelamin" htmlFor="gender" error={err("gender")}>
        <div className="relative">
          <select id="gender" className={`${inputClass} appearance-none pr-10`} {...register("gender")}>
            <option value="male">Laki-laki</option>
            <option value="female">Perempuan</option>
          </select>
          <ChevronDown aria-hidden className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-theme-gray" />
        </div>
      </AuthField>

      <AuthField label="Kata Sandi" htmlFor="password" error={err("password")}>
        <PasswordInput
          id="password"
          autoComplete="new-password"
          invalid={!!err("password")}
          {...register("password", { required: "Kata sandi wajib diisi" })}
        />
      </AuthField>
      <AuthField label="Konfirmasi Kata Sandi" htmlFor="password_confirmation" error={err("password_confirmation")}>
        <PasswordInput
          id="password_confirmation"
          autoComplete="new-password"
          invalid={!!err("password_confirmation")}
          {...register("password_confirmation", {
            required: "Konfirmasi kata sandi wajib diisi",
            validate: (v) => v === getValues("password") || "Konfirmasi kata sandi tidak sama",
          })}
        />
      </AuthField>

      <SubmitButton loading={mutation.isLoading || mutation.isSuccess}>Daftar</SubmitButton>
      <OrDivider />
      <GoogleButton redirect={redirect} />
    </form>
  );
}
